import { translateBackend } from '@assets/i18n/config';
import type { TranslatedString } from '@interfaces/shared';

export const GOAL_RISK_QUESTION_SUB_STEP_TYPE = 'GOAL_RISK_QUESTION_1';

export type EmbeddedWorkflowResolutionStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'error';

export type WorkflowSubStepsDefinition = {
  steps?: {
    subSteps?: {
      type?: string;
      options?: GoalRiskSubStepOptions;
    }[];
  }[];
};

export type GoalRiskSubStepOptions = {
  title?: TranslatedString;
  options?: {
    value: string;
    label?: TranslatedString;
    description?: TranslatedString;
  }[];
};

export type ReviewGoalRiskTitleResolution = {
  riskQuestionTitle?: TranslatedString;
  riskQuestionTitlePending: boolean;
};

const hasTranslatedContent = (
  value: TranslatedString | undefined,
): value is TranslatedString => Boolean(value && translateBackend(value));

export const findWorkflowSubStepOptions = (
  workflow: { workflow?: WorkflowSubStepsDefinition },
  subStepType: string,
): GoalRiskSubStepOptions | undefined =>
  workflow.workflow?.steps
    ?.flatMap((step) => step?.subSteps ?? [])
    .find((subStep) => subStep?.type === subStepType)?.options;

export const findEmbeddedGoalRiskSubStepOptions = (
  embeddedWorkflow?: WorkflowSubStepsDefinition | null,
): GoalRiskSubStepOptions | undefined => {
  if (!embeddedWorkflow) {
    return undefined;
  }

  return findWorkflowSubStepOptions(
    { workflow: embeddedWorkflow },
    GOAL_RISK_QUESTION_SUB_STEP_TYPE,
  );
};

export const resolveGoalRiskQuestionTitle = (
  sessionTitle?: TranslatedString,
  embeddedSubstepTitle?: TranslatedString,
): TranslatedString | undefined => {
  if (hasTranslatedContent(embeddedSubstepTitle)) {
    return embeddedSubstepTitle;
  }
  if (hasTranslatedContent(sessionTitle)) {
    return sessionTitle;
  }
  return undefined;
};

export const resolveReviewGoalRiskQuestionTitle = ({
  sessionTitle,
  embeddedWorkflow,
  embeddedWorkflowStatus,
}: {
  sessionTitle?: TranslatedString;
  embeddedWorkflow?: WorkflowSubStepsDefinition | null;
  embeddedWorkflowStatus: EmbeddedWorkflowResolutionStatus;
}): ReviewGoalRiskTitleResolution => {
  const embeddedTitle =
    findEmbeddedGoalRiskSubStepOptions(embeddedWorkflow)?.title;
  const resolvedTitle = resolveGoalRiskQuestionTitle(
    sessionTitle,
    embeddedTitle,
  );

  if (resolvedTitle) {
    return {
      riskQuestionTitle: resolvedTitle,
      riskQuestionTitlePending: false,
    };
  }

  if (embeddedWorkflowStatus === 'loading') {
    return {
      riskQuestionTitle: undefined,
      riskQuestionTitlePending: true,
    };
  }

  return {
    riskQuestionTitle: undefined,
    riskQuestionTitlePending: false,
  };
};
